import { Suspense, type ReactNode } from "react";
import { lazyWithReload } from "@/lib/lazyWithReload";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ToastPositionSync } from "@/features/settings/ToastPositionSync";
import { AppUpdateSync } from "@/features/settings/AppUpdateSync";
import { PointerLockGuard } from "@/components/PointerLockGuard";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ConfirmDialogProvider } from "@/components/ui/confirm-dialog";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { toast } from "sonner";
import { getActivePublicLanguage } from "@/lib/publicLanguage";
import { t as translate } from "@/lib/i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { GeocodingProvider } from "@/contexts/GeocodingContext";
import { FeatureProvider } from "@/contexts/FeatureContext";
import { PermissionProvider, usePermissionContext } from "@/contexts/PermissionContext";
import { firstAccessibleRoute } from "@/config/routePermissions";
import { Loader2 } from "@/components/icons";
import { FeatureGate } from "@/components/ui/FeatureGate";
import { FeatureToggleGate } from "@/features/featureToggles/FeatureToggleGate";
import { ProtectedRoute } from "@/components/ProtectedRoute";
const Layout = lazyWithReload(() => import("@/components/layout/Layout").then(module => ({ default: module.Layout })));
import { usePageTracking } from "@/hooks/usePageTracking";
import { Analytics } from "@vercel/analytics/react";
const DemoLibraryPage = lazyWithReload(() => import("./features/demo-library/DemoLibraryPage"));
const DemoLibraryManager = lazyWithReload(() => import("./features/demo-library/DemoLibraryManager"));

const Dashboard = lazyWithReload(() => import("./pages/Dashboard"));
const Welcome = lazyWithReload(() => import("./pages/Welcome"));
import { useWizardStatus } from "@/features/onboarding-wizard/useWizard";
import { shouldRedirectToWizard } from "@/features/onboarding-wizard/steps";
const Auth = lazyWithReload(() => import("./pages/Auth"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));
const NotAuthorized = lazyWithReload(() => import("./pages/NotAuthorized"));
const SchedulingPage = lazyWithReload(() => import("./pages/SchedulingPage"));
const Inventory = lazyWithReload(() => import("./pages/Inventory"));
const QuoteDetail = lazyWithReload(() => import("./pages/QuoteDetail"));
const QuoteBuilder = lazyWithReload(() => import("./pages/QuoteBuilder"));
const Invoices = lazyWithReload(() => import("./pages/Invoices"));
const InvoiceDetail = lazyWithReload(() => import("./pages/InvoiceDetail"));
const InvoiceBuilder = lazyWithReload(() => import("./pages/InvoiceBuilder"));
const InvoicePublic = lazyWithReload(() => import("./pages/InvoicePublic"));
const MockupPublic = lazyWithReload(() => import("./pages/MockupPublic"));
const CRM = lazyWithReload(() => import("./pages/CRM"));
const CustomerImport = lazyWithReload(() => import("./components/crm/CustomerImport").then(module => ({ default: module.CustomerImport })));
const Payments = lazyWithReload(() => import("./pages/Payments"));
const Marketing = lazyWithReload(() => import("./pages/Marketing"));
const Messages = lazyWithReload(() => import("./pages/Messages"));
const Team = lazyWithReload(() => import("./pages/Team"));
const TeamMemberDetail = lazyWithReload(() => import("./pages/TeamMemberDetail"));
const Measurements = lazyWithReload(() => import("./pages/Measurements"));
const MeasurementDetail = lazyWithReload(() => import("./pages/MeasurementDetail"));
const QuickMeasureTreeEstimator = lazyWithReload(() => import("./pages/QuickMeasureTreeEstimator"));
const MockupsList = lazyWithReload(() => import("./pages/MockupsList"));
const MockupNew = lazyWithReload(() => import("./pages/MockupNew"));
const MockupAiNew = lazyWithReload(() => import("./pages/MockupAiNew"));
const MockupDetail = lazyWithReload(() => import("./pages/MockupDetail"));
const CrewAvailabilityPage = lazyWithReload(() => import("./pages/CrewAvailability"));
const CrewDashboard = lazyWithReload(() => import("./pages/CrewDashboard"));
import { PLATFORM_ROLES } from "@/features/team/clock/myJobsRoles";
const CrewInviteAcceptance = lazyWithReload(() => import("./pages/CrewInviteAcceptance"));
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword"));
const UnsubscribeUpdates = lazyWithReload(() => import("./pages/UnsubscribeUpdates"));
const CompleteGoogleSignup = lazyWithReload(() => import("./pages/CompleteGoogleSignup"));
const Jobs = lazyWithReload(() => import("./pages/Jobs"));
const JobsIndex = lazyWithReload(() => import("./pages/Jobs").then(module => ({ default: module.JobsIndex })));
const JobDetail = lazyWithReload(() => import("./pages/JobDetail"));
const ServiceRequests = lazyWithReload(() => import("./pages/ServiceRequests"));
const ServiceRequestDetail = lazyWithReload(() => import("./pages/ServiceRequestDetail"));
const DebugCrew = lazyWithReload(() => import("./pages/DebugCrew"));
const Settings = lazyWithReload(() => import("./pages/Settings"));
const HighLevel = lazyWithReload(() => import("./pages/HighLevel"));
const BusinessSuiteRedirect = lazyWithReload(() => import("./pages/BusinessSuiteRedirect"));
const LeadCollection = lazyWithReload(() => import("./pages/LeadCollection"));
const LeadCollectionPublic = lazyWithReload(() => import("./pages/LeadCollectionPublic"));
const RoutePlanner = lazyWithReload(() => import("./pages/Routes"));
const SuperAdminDashboard = lazyWithReload(() => import("./pages/super-admin/SuperAdminDashboard").then(module => ({ default: module.SuperAdminDashboard })));
const SuperAdminOrganizationsPage = lazyWithReload(() => import("./pages/super-admin/SuperAdminOrganizationsPage").then(module => ({ default: module.SuperAdminOrganizationsPage })));
const SuperAdminPlansPage = lazyWithReload(() => import("./pages/super-admin/SuperAdminPlansPage").then(module => ({ default: module.SuperAdminPlansPage })));
const SuperAdminFeaturesPage = lazyWithReload(() => import("./pages/super-admin/SuperAdminFeaturesPage").then(module => ({ default: module.SuperAdminFeaturesPage })));
const SuperAdminUsersPage = lazyWithReload(() => import("./pages/super-admin/SuperAdminUsersPage").then(module => ({ default: module.SuperAdminUsersPage })));
const SuperAdminAnnouncementsPage = lazyWithReload(() => import("./pages/super-admin/SuperAdminAnnouncementsPage").then(module => ({ default: module.SuperAdminAnnouncementsPage })));
const SuperAdminChangelogPage = lazyWithReload(() => import("./pages/super-admin/SuperAdminChangelogPage").then(module => ({ default: module.SuperAdminChangelogPage })));
const SuperAdminMigrationsPage = lazyWithReload(() => import("./pages/super-admin/SuperAdminMigrationsPage").then(module => ({ default: module.SuperAdminMigrationsPage })));
const SuperAdminMarketingPage = lazyWithReload(() => import("./pages/super-admin/SuperAdminMarketingPage").then(module => ({ default: module.SuperAdminMarketingPage })));
const MarketingCampaignDetail = lazyWithReload(() => import("./pages/super-admin/MarketingCampaignDetail").then(module => ({ default: module.MarketingCampaignDetail })));
const MarketingContactDetail = lazyWithReload(() => import("./pages/super-admin/MarketingContactDetail").then(module => ({ default: module.MarketingContactDetail })));
const OrganizationDetail = lazyWithReload(() => import("./pages/super-admin/OrganizationDetail").then(module => ({ default: module.OrganizationDetail })));
const SuperAdminAI = lazyWithReload(() => import("./pages/super-admin/SuperAdminAI").then(module => ({ default: module.SuperAdminAI })));
const UserDetail = lazyWithReload(() => import("./pages/super-admin/UserDetail").then(module => ({ default: module.UserDetail })));
const AgencyMarketingPage = lazyWithReload(() => import("./pages/agency/AgencyMarketingPage").then(module => ({ default: module.AgencyMarketingPage })));
const AffiliateInviteAcceptance = lazyWithReload(() => import("./pages/AffiliateInviteAcceptance"));
const AffiliateDashboard = lazyWithReload(() => import("./pages/affiliate/AffiliateDashboard").then(module => ({ default: module.AffiliateDashboard })));
const SuperAdminSupport = lazyWithReload(() => import("./pages/super-admin/SuperAdminSupport").then(module => ({ default: module.SuperAdminSupport })));
const SuperAdminVoice = lazyWithReload(() => import("./pages/super-admin/SuperAdminVoice").then(module => ({ default: module.SuperAdminVoice })));
const SuperAdminCalls = lazyWithReload(() => import("./pages/super-admin/SuperAdminCalls").then(module => ({ default: module.SuperAdminCalls })));
const SuperAdminDemos = lazyWithReload(() => import("./pages/super-admin/SuperAdminDemos").then(module => ({ default: module.SuperAdminDemos })));
const SuperAdminActivity = lazyWithReload(() => import("./pages/super-admin/SuperAdminActivity").then(module => ({ default: module.SuperAdminActivity })));
const SuperAdminSales = lazyWithReload(() => import("./pages/super-admin/SuperAdminSales").then(module => ({ default: module.SuperAdminSales })));
const SuperAdminRevenue = lazyWithReload(() => import("./pages/super-admin/SuperAdminRevenue").then(module => ({ default: module.SuperAdminRevenue })));
const SuperAdminPipeline = lazyWithReload(() => import("./pages/super-admin/SuperAdminPipeline").then(module => ({ default: module.SuperAdminPipeline })));
const SuperAdminReferrals = lazyWithReload(() => import("./pages/super-admin/SuperAdminReferrals").then(module => ({ default: module.SuperAdminReferrals })));
const SuperAdminErrors = lazyWithReload(() => import("./pages/super-admin/SuperAdminErrors").then(module => ({ default: module.SuperAdminErrors })));
const SuperAdminAutoReply = lazyWithReload(() => import("./pages/super-admin/SuperAdminAutoReply").then(module => ({ default: module.SuperAdminAutoReply })));
const SuperAdminSettingsPage = lazyWithReload(() => import("./pages/super-admin/SuperAdminSettingsPage").then(module => ({ default: module.SuperAdminSettingsPage })));
const Support = lazyWithReload(() => import("./pages/Support"));
const SupportTicketDetail = lazyWithReload(() => import("./pages/SupportTicketDetail"));
const Help = lazyWithReload(() => import("./pages/Help"));
const WhatsNew = lazyWithReload(() => import("./pages/WhatsNew"));
const Activity = lazyWithReload(() => import("./pages/Activity"));
const Pipeline = lazyWithReload(() => import("./pages/Pipeline"));
const Proposals = lazyWithReload(() => import("./pages/Proposals"));
const AutoProposal = lazyWithReload(() => import("./features/auto-proposal/AutoProposal"));
const ProposalNew = lazyWithReload(() => import("./pages/ProposalNew"));
const ProposalDetail = lazyWithReload(() => import("./pages/ProposalDetail"));
const ProposalPublicNightfall = lazyWithReload(() => import("./pages/ProposalPublicNightfall"));
// Lazy: the portal pages are the ONLY importers of clientPortalClient (a
// second GoTrueClient). Keeping them out of the main bundle means the admin
// app never instantiates it (2026-07-22 session-poisoning incident).
//
// lazyWithReload (the SHARED one — src/lib/lazyWithReload.ts): a tab that
// predates the latest deploy resolves these routes' chunks to a 404 or to
// index.html, and the root boundary would render "Something went wrong"
// (the /lumen crash report). The shared helper validates the module, reloads
// once behind the same throttle main.tsx uses, and rethrows on a genuinely
// broken deploy.
const LumenPage = lazyWithReload(() => import("./pages/LumenPage"));
const StandardCustomerPortal = lazyWithReload(() => import("./pages/StandardCustomerPortal"));
const StandardCustomerPortalSection = lazyWithReload(() => import("./pages/StandardCustomerPortalSection"));
const StandardCustomerPortalSettings = lazyWithReload(() => import("./pages/StandardCustomerPortalSettings"));
const PaymentLinkPublic = lazyWithReload(() => import("./pages/PaymentLinkPublic"));
const InvoicePayPublic = lazyWithReload(() => import("./pages/InvoicePayPublic"));
const Expenses = lazyWithReload(() => import("./pages/Expenses"));
const SatelliteMeasure = lazyWithReload(() => import("./pages/SatelliteMeasure"));
const MeasurementEditor = lazyWithReload(() => import("./pages/MeasurementEditor"));
const Reports = lazyWithReload(() => import("./pages/Reports"));
const Reputation = lazyWithReload(() => import("./pages/Reputation"));
const DoorDrop = lazyWithReload(() => import("./pages/DoorDrop"));
const DoorDropNewCampaign = lazyWithReload(() => import("./pages/DoorDropNewCampaign"));
const DoorDropCampaign = lazyWithReload(() => import("./pages/DoorDropCampaign"));
const DoorDropLanding = lazyWithReload(() => import("./pages/DoorDropLanding"));
const DoorDropPublic = lazyWithReload(() => import("./pages/DoorDropPublic"));
// Business texting opt-in page: loaded only when visited; renders NotFound for
// any organization without a live consent form.
const PublicSmsConsent = lazyWithReload(() => import("./features/messaging/sms/PublicSmsConsent"));
// #1335 customer sign-off by link (anon, token-scoped).
const SignoffPublic = lazyWithReload(() => import("./features/jobs/signoff/public/SignoffPublic"));
import { DOORDROP_ENABLED } from "./features/doordrop/flags";
import { HELP_CENTER_ENABLED } from "./features/help/flags";
const EmailConfirmation = lazyWithReload(() => import("./pages/EmailConfirmation"));
const MockupsLite = lazyWithReload(() => import("./pages/MockupsLite"));
const SubscriptionManagement = lazyWithReload(() => import("./pages/SubscriptionManagement"));
const CancelPlan = lazyWithReload(() => import("./pages/CancelPlan"));
const CheckoutSuccess = lazyWithReload(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazyWithReload(() => import("./pages/CheckoutCancel"));
const QuickBooksConnect = lazyWithReload(() => import("./pages/QuickBooksConnect"));
const QuickBooksDisconnect = lazyWithReload(() => import("./pages/QuickBooksDisconnect"));
const QuickBooksSyncLog = lazyWithReload(() => import("./pages/QuickBooksSyncLog"));
const QuickBooksMappingSettings = lazyWithReload(() => import("./components/settings/QuickBooksMappingSettings"));

const queryClient = new QueryClient({
  // Surface read failures instead of silently showing empty/"no data". A failed
  // query/RPC now raises one deduped, user-friendly toast (no raw error leaked).
  queryCache: new QueryCache({
    onError: () => {
      // A public page (client portal) sets its language; admin pages stay English (audit D11).
      const lang = getActivePublicLanguage();
      toast.error(lang ? translate(lang, 'portal.common.load_error_toast') : "Couldn't load some data. Check your connection and try again.", {
        id: 'query-error',
      });
    },
  }),
  // Global 5-minute freshness so signed storage URLs (mockups, attachments,
  // design photos) stop re-minting on every window refocus (#5). Queries that
  // need immediacy set their own staleTime:0 and keep it — this is only the
  // default for the many that specify none.
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
    },
  },
});

const PageTracker = () => {
  usePageTracking();
  return null;
};

// Redirect that preserves the current querystring. Used for legacy entry
// points so context params (e.g. ?customerId) are not dropped on the hop.
const PreserveSearchRedirect = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

// Route-level Suspense for EVERY lazy route element (see lazy imports above).
// A lazy element with no boundary above it throws React #426 when reached by a
// click (a synchronous update), which the error boundary shows as "Something
// went wrong" — that is how /super-admin/demo-library broke.
// Same centered spinner the app shows while permissions load.
const PortalSuspense = ({ children }: { children: ReactNode }) => (
  <Suspense
    fallback={
      <div role="status" aria-label="Loading page" className="min-h-[50vh] flex items-center justify-center bg-gradient-subtle">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    }
  >
    {children}
  </Suspense>
);

/** /crew-dashboard: every org role (crew, admin, user, custom); platform roles go home. */
const MyJobsRoute = () => {
  const { role } = useAuth();
  // Wait for the role (the old allowedRoles gate did too), so an admin never
  // sees a flash of the crew onboarding check.
  if (!role) {
    return (
      <div role="status" aria-label="Loading page" className="min-h-[50vh] flex items-center justify-center bg-gradient-subtle">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (PLATFORM_ROLES.includes(role)) return <Navigate to="/" replace />;
  return <PortalSuspense><CrewDashboard /></PortalSuspense>;
};

const DashboardWrapper = () => {
  const { role } = useAuth();
  const { can, isLoading: permissionsLoading } = usePermissionContext();
  // First-run proposal wizard: an org admin whose org has produced nothing yet
  // (or who left the wizard mid-way) lands in /welcome instead of an empty
  // dashboard. Only admins are asked; completed/dismissed rows never re-open.
  const wizard = useWizardStatus(role === 'admin');

  if (role === 'crew') {
    return <PortalSuspense><CrewDashboard /></PortalSuspense>;
  }

  if (role === 'super_admin') {
    return <Navigate to="/super-admin" replace />;
  }

  if (role === 'support_staff') {
    return <Navigate to="/super-admin/support" replace />;
  }

  if (role === 'marketing_agency') {
    return <Navigate to="/agency" replace />;
  }

  if (role === 'affiliate') {
    return <Navigate to="/affiliate" replace />;
  }

  // Built-in admin/user always have tab.dashboard. A custom role might not — in
  // that case route them to the first tab they CAN open instead of dead-ending
  // on /forbidden. Wait for permissions so we don't flash the dashboard first.
  if (permissionsLoading) {
    return (
      <div role="status" aria-label="Loading page" className="min-h-[50vh] flex items-center justify-center bg-gradient-subtle">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (role === 'admin') {
    if (wizard.isLoading) {
      return (
        <div role="status" aria-label="Loading page" className="min-h-[50vh] flex items-center justify-center bg-gradient-subtle">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }
    if (shouldRedirectToWizard(wizard.data, wizard.isError)) {
      return <Navigate to="/welcome" replace />;
    }
  }
  if (can('tab.dashboard')) {
    return <PortalSuspense><Dashboard /></PortalSuspense>;
  }
  const landing = firstAccessibleRoute(can);
  if (landing) {
    return <Navigate to={landing} replace />;
  }
  // No tabs at all (mis-configured role): show a clear message instead of a blank.
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-subtle px-6">
      <div className="text-center max-w-md">
        <h1 className="text-xl font-semibold mb-2">No pages enabled</h1>
        <p className="text-muted-foreground text-sm">
          Your role doesn’t have access to any pages yet. Ask an administrator to grant your
          role at least one tab in Settings → Roles &amp; Permissions.
        </p>
      </div>
    </div>
  );
};

// Public proposal route. There is ONE renderer: the themed page reads its
// theme from get_proposal_public_theme and clamps it through the registry in
// features/proposals/public/themes.ts. The old white-card page is retired to
// src/graveyard/ProposalPublic.tsx.
//
// `?preview=nightfall` is now a harmless no-op — links handed out during the
// rollout still resolve to the same page rather than 404ing. `?theme=<key>`
// IS still meaningful: the page treats it as a non-persisted preview
// override (see ProposalPublicNightfall).
const ProposalPublicRoute = () => <PortalSuspense><ProposalPublicNightfall /></PortalSuspense>;

// /estimates/:param serves TWO worlds: public share links (24-char base64
// tokens, never UUID-shaped) and the admin detail page (/estimates/<uuid>).
// This public route is declared first and was swallowing admin deep links —
// Lumen's "View estimate" chips landed on "This document isn't available".
// A UUID param is unambiguously an admin id: bounce it to the legacy
// /quotes/:quoteId route, which renders the same QuoteDetail.
const UUID_PARAM = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EstimateTokenOrIdRoute = () => {
  const { token } = useParams<{ token: string }>();
  const { search, hash } = useLocation();
  if (token && UUID_PARAM.test(token)) {
    // Preserve the query string + hash (e.g. ?openEmail=true) so admin deep
    // links like "Save & send" still open their dialog after the redirect.
    return <Navigate to={`/quotes/${token}${search}${hash}`} replace />;
  }
  return <ProposalPublicRoute />;
};

// Last-resort fallback when an uncaught render error escapes every route's
// own handling. Without this, a single thrown error blanks the whole app to a
// white screen with no recovery. Static (no router/query hooks) so it can sit
// at the very top of the tree.
const RootErrorFallback = () => (
  <div className="min-h-screen flex items-center justify-center p-6 bg-background">
    <div className="max-w-md text-center space-y-4">
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="text-sm text-muted-foreground">
        The app hit an unexpected error. Reloading usually fixes it. If it keeps
        happening, contact support.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex items-center justify-center rounded-full bg-primary px-5 h-10 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
      >
        Reload the app
      </button>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <TooltipProvider>
        <AuthProvider>
          <FeatureProvider>
            <PermissionProvider>
            <GeocodingProvider>
              <ConfirmDialogProvider>
              <Sonner />
              <ToastPositionSync />
              <ErrorBoundary fallback={<RootErrorFallback />}>
              <BrowserRouter>
                <PageTracker />
                <AppUpdateSync />
                <PointerLockGuard />
                <Analytics />
            <Routes>
            <Route path="/auth" element={<PortalSuspense><Auth /></PortalSuspense>} />
            <Route path="/auth/confirm-email" element={<PortalSuspense><EmailConfirmation /></PortalSuspense>} />
            <Route path="/auth/reset-password" element={<PortalSuspense><ResetPassword /></PortalSuspense>} />
            {/* One-click link from the changelog update emails; no login. */}
            <Route path="/unsubscribe/updates" element={<PortalSuspense><UnsubscribeUpdates /></PortalSuspense>} />
            <Route path="/auth/complete-profile" element={<PortalSuspense><CompleteGoogleSignup /></PortalSuspense>} />
            <Route path="/crew/accept-invitation" element={<PortalSuspense><CrewInviteAcceptance /></PortalSuspense>} />
            <Route path="/affiliate/accept-invitation" element={<PortalSuspense><AffiliateInviteAcceptance /></PortalSuspense>} />
            {import.meta.env.DEV && (
              <Route path="/debug-crew" element={<PortalSuspense><DebugCrew /></PortalSuspense>} />
            )}
            <Route path="/p/:organizationSlug/:campaignSlug" element={<PortalSuspense><DoorDropPublic /></PortalSuspense>} />
            <Route path="/sms/subscribe/:organizationId" element={<PortalSuspense><PublicSmsConsent /></PortalSuspense>} />
            <Route path="/i/:token" element={<PortalSuspense><InvoicePublic /></PortalSuspense>} />
            <Route path="/signoff/:token" element={<PortalSuspense><SignoffPublic /></PortalSuspense>} />
            <Route path="/proposal/:token" element={<ProposalPublicRoute />} />
            {/* N1: per-mockup public share link */}
            <Route path="/m/:token" element={<PortalSuspense><MockupPublic /></PortalSuspense>} />
            {/* Estimate shares mint /estimates/:token now — same page, honest
                URL. Old /proposal/:token estimate links keep resolving. */}
            <Route path="/estimates/:token" element={<EstimateTokenOrIdRoute />} />
            <Route path="/portal/o/:orgSlug" element={<PortalSuspense><StandardCustomerPortal /></PortalSuspense>} />
            <Route path="/portal/o/:orgSlug/messages" element={<PortalSuspense><StandardCustomerPortalSection section="messages" /></PortalSuspense>} />
            <Route path="/portal/o/:orgSlug/payments" element={<PortalSuspense><StandardCustomerPortalSection section="payments" /></PortalSuspense>} />
            <Route path="/portal/o/:orgSlug/proposals" element={<PortalSuspense><StandardCustomerPortalSection section="proposals" /></PortalSuspense>} />
            <Route path="/portal/o/:orgSlug/service-requests" element={<PortalSuspense><StandardCustomerPortalSection section="service-requests" /></PortalSuspense>} />
            <Route path="/portal/o/:orgSlug/photos" element={<PortalSuspense><StandardCustomerPortalSection section="photos" /></PortalSuspense>} />
            <Route path="/portal/o/:orgSlug/settings" element={<PortalSuspense><StandardCustomerPortalSettings /></PortalSuspense>} />
            {/* The per-customer direct-link portal (/portal/:token) was retired in
                favor of the org portal where the customer enters their phone/email
                (/portal/o/:orgSlug). The old token pages live in src/graveyard. */}
            {/* /pay/invoice/:token must precede /pay/:token so it isn't shadowed */}
            <Route path="/pay/invoice/:token" element={<PortalSuspense><InvoicePayPublic /></PortalSuspense>} />
            <Route path="/pay/:token" element={<PortalSuspense><PaymentLinkPublic /></PortalSuspense>} />
            <Route path="/lead/:slug" element={<PortalSuspense><LeadCollectionPublic /></PortalSuspense>} />

            {/* First-run proposal wizard — full screen, no Layout on purpose */}
            <Route path="/welcome" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PortalSuspense><Welcome /></PortalSuspense>
              </ProtectedRoute>
            } />

            {/* Subscription Management Routes - No subscription required */}
            <Route path="/subscription-management" element={
              <ProtectedRoute requiresSubscription={false}>
                <PortalSuspense><SubscriptionManagement /></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/cancel-plan" element={
              <ProtectedRoute requiresSubscription={false}>
                <PortalSuspense><CancelPlan /></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/checkout/success" element={
              <ProtectedRoute requiresSubscription={false}>
                <PortalSuspense><CheckoutSuccess /></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/checkout/cancel" element={
              <ProtectedRoute requiresSubscription={false}>
                <PortalSuspense><CheckoutCancel /></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/quickbooks/connect" element={
              <ProtectedRoute allowedRoles={["admin","super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><QuickBooksConnect /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/quickbooks/disconnect" element={
              <ProtectedRoute allowedRoles={["admin","super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><QuickBooksDisconnect /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />

            <Route path="/crew-dashboard" element={
              // Crew dashboard for crew; "My jobs" for admins, users and custom
              // roles (#1456). No allowedRoles: that list cannot name custom role
              // keys. MyJobsRoute sends platform roles to their portal.
              <ProtectedRoute>
                <PortalSuspense><Layout>
                  <MyJobsRoute />
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/" element={
              // No allowedRoles: any authenticated org member reaches DashboardWrapper,
              // which routes them by role/permission (platform portals, crew dashboard,
              // the dashboard if permitted, else their first accessible tab). This keeps
              // custom roles from dead-ending on /forbidden right after login.
              <ProtectedRoute>
                <PortalSuspense><Layout>
                  <DashboardWrapper />
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* New Jobs parent with nested sub-routes */}
            <Route path="/jobs" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Jobs /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            }>
              <Route index element={<PortalSuspense><JobsIndex /></PortalSuspense>} />
              <Route path="scheduling" element={
                <ProtectedRoute allowedRoles={["admin","user"]}>
                  <PortalSuspense><SchedulingPage /></PortalSuspense>
                </ProtectedRoute>
              } />
            </Route>
            <Route path="/jobs/:jobId" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><JobDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/service-requests" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><ServiceRequests /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/service-requests/:id" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><ServiceRequestDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/crm" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><CRM /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/crm/import" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><CustomerImport /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/crm/:customerId" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><CRM /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* The standalone estimates LIST is retired — the Proposals &
                Estimates tab (/proposals) is the single list and already shows
                every estimate. The /estimates/* document routes below stay. */}
            <Route path="/estimates" element={<PreserveSearchRedirect to="/proposals" />} />
            <Route path="/estimates/new" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><QuoteBuilder /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/estimates/:quoteId/edit" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><QuoteBuilder /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/estimates/:quoteId" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><QuoteDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/quotes" element={<PreserveSearchRedirect to="/proposals" />} />
            <Route path="/quotes/new" element={<PreserveSearchRedirect to="/estimates/new" />} />
            <Route path="/quotes/:quoteId/edit" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><QuoteBuilder /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/quotes/:quoteId" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><QuoteDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/invoices" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Invoices /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/invoices/new" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><InvoiceBuilder /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/invoices/:invoiceId/edit" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><InvoiceBuilder /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/invoices/:invoiceId" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><InvoiceDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/measurements" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Measurements /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* Legacy route — picker is now a dialog on /measurements.
                Preserve search so ?customerId/new context isn't dropped. */}
            <Route path="/measurements/new" element={<PreserveSearchRedirect to="/measurements" />} />
            <Route path="/measurements/new/photo" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MeasurementEditor /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/measurements/new/satellite" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MeasurementEditor /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/measurements/new/both" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MeasurementEditor /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* Legacy photo-measure screen retired — everyone (incl. crew) uses the
                unified /measurements/new flow. Old links redirect to the hub. */}
            <Route path="/measurements/new/legacy" element={<PreserveSearchRedirect to="/measurements" />} />
            <Route path="/measurements/tree-estimator" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><QuickMeasureTreeEstimator /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/measurements/:id/satellite" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SatelliteMeasure /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/measurements/:measurementId/edit" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MeasurementEditor /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/measurements/:measurementId" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MeasurementDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/lumen" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><LumenPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/mockups" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MockupsList /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/mockups/new" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MockupNew /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* AI-Assisted (preset-driven) creation — parallel flow to the
                manual editor. The "New Mockup" button forks into Manual vs AI. */}
            <Route path="/mockups/ai" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MockupAiNew /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/mockups/new/start" element={<PreserveSearchRedirect to="/mockups/new" />} />
            <Route path="/mockups/:mockupId" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MockupDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/mockups-lite" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <FeatureGate feature="mockups_lite">
                    <PortalSuspense><MockupsLite /></PortalSuspense>
                  </FeatureGate>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* Legacy standalone measure tool retired — crews now use the unified
                measurement flow (crew access enabled above). Graveyarded in
                src/graveyard/quickmeasure-v2. */}
            <Route path="/measure" element={<PreserveSearchRedirect to="/measurements" />} />
            <Route path="/payments" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Payments /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/inventory" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Inventory /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* Backwards-compatible redirects from old paths */}
            <Route path="/calendar" element={<Navigate to="/jobs/scheduling" replace />} />
            <Route path="/jobs/calendar" element={<Navigate to="/jobs/scheduling" replace />} />
            <Route path="/map" element={<Navigate to="/jobs" replace />} />
            <Route path="/jobs/map" element={<Navigate to="/jobs" replace />} />
            <Route path="/crew" element={<Navigate to="/team" replace />} />
            <Route path="/crew/availability" element={<Navigate to="/team/availability" replace />} />
            <Route path="/routes" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><RoutePlanner /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/marketing" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Marketing /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {DOORDROP_ENABLED && (<>
            <Route path="/marketing/doordrop" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><DoorDrop /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/marketing/doordrop/new" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><DoorDropNewCampaign /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/marketing/doordrop/landing" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><DoorDropLanding /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/marketing/doordrop/campaigns/:campaignId" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><DoorDropCampaign /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            </>)}
            <Route path="/marketing/lead-collection" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><LeadCollection /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/messages" element={
              <ProtectedRoute allowedRoles={["admin", "user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Messages /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/team" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <PortalSuspense><Layout>
                  <FeatureGate feature="team_management">
                    <PortalSuspense><Team /></PortalSuspense>
                  </FeatureGate>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/team/availability" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><CrewAvailabilityPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* Team & roles is a Settings tab now, not its own page. Old
                bookmarks and in-app links land on the tab; the tab itself
                re-applies the admin + action.roles.manage guard. */}
            <Route path="/settings/roles" element={<Navigate to="/settings?tab=roles" replace />} />
            <Route path="/settings/quickbooks-mapping" element={
              <ProtectedRoute allowedRoles={["admin","super_admin"]}>
                <PortalSuspense><Layout>
                  {/* Feature-gated like the integration card itself — an admin
                      on a plan without QuickBooks shouldn't reach it by URL. */}
                  <FeatureGate feature="quickbooks">
                    <div className="p-6">
                      <PortalSuspense><QuickBooksMappingSettings /></PortalSuspense>
                    </div>
                  </FeatureGate>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/settings/quickbooks-sync-log" element={
              <ProtectedRoute allowedRoles={["admin","super_admin"]}>
                <PortalSuspense><Layout>
                  <FeatureGate feature="quickbooks">
                    <PortalSuspense><QuickBooksSyncLog /></PortalSuspense>
                  </FeatureGate>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/team/:userId" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><TeamMemberDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Settings /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* HighLevel workspace — sidebar tab exists only for a connected org;
                the page itself bounces to Settings → Integrations otherwise. */}
            <Route path="/highlevel" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><HighLevel /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* Business Suite retired — redirect legacy deep links into Settings. */}
            <Route path="/business-suite" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><BusinessSuiteRedirect /></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute allowedRoles={["admin","super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Support /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/support/:ticketId" element={
              <ProtectedRoute allowedRoles={["admin","super_admin","support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SupportTicketDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/activity" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Activity /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/pipeline" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Pipeline /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/proposals" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Proposals /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/proposals/auto" element={<ProtectedRoute allowedRoles={["admin", "user"]}><PortalSuspense><Layout><PortalSuspense><AutoProposal /></PortalSuspense></Layout></PortalSuspense></ProtectedRoute>} />
            <Route path="/proposals/new" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  {/* Proposals are Standard+ (#274). The list at
                      /proposals stays open — on Lite it is the
                      Estimates view — but the proposal builder and
                      detail are the entitlement. */}
                  <FeatureGate feature="proposals">
                    <PortalSuspense><ProposalNew /></PortalSuspense>
                  </FeatureGate>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/proposals/new/proposal" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  {/* Proposals are Standard+ (#274). The list at
                      /proposals stays open — on Lite it is the
                      Estimates view — but the proposal builder and
                      detail are the entitlement. */}
                  <FeatureGate feature="proposals">
                    <PortalSuspense><ProposalNew /></PortalSuspense>
                  </FeatureGate>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/proposals/:id" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  {/* Proposals are Standard+ (#274). The list at
                      /proposals stays open — on Lite it is the
                      Estimates view — but the proposal builder and
                      detail are the entitlement. */}
                  <FeatureGate feature="proposals">
                    <PortalSuspense><ProposalDetail /></PortalSuspense>
                  </FeatureGate>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Reports /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/expenses" element={
              <ProtectedRoute allowedRoles={["admin","user","crew"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Expenses /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/reputation" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Reputation /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/reputation/:tab" element={
              <ProtectedRoute allowedRoles={["admin","user"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><Reputation /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminDashboard /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/organizations" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminOrganizationsPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/organizations/:orgId" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><OrganizationDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/plans" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminPlansPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/features" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminFeaturesPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/users" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminUsersPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/users/:userId" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><UserDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/voice" element={<ProtectedRoute allowedRoles={["super_admin", "support_staff"]}><PortalSuspense><Layout><PortalSuspense><SuperAdminVoice /></PortalSuspense></Layout></PortalSuspense></ProtectedRoute>} />
            <Route path="/super-admin/calls" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminCalls /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/activity" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminActivity /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/sales" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminSales /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/revenue" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminRevenue /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/pipeline" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminPipeline /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/demos" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminDemos /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/referrals" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminReferrals /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/support/auto-reply" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminAutoReply /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/errors" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminErrors /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/announcements" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminAnnouncementsPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/changelog" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminChangelogPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/migrations" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminMigrationsPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/marketing" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminMarketingPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/marketing/campaigns/:campaignId" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MarketingCampaignDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/marketing/contacts/:contactId" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MarketingContactDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* Marketing-agency surface: same workspace, no super-admin shell/nav. */}
            <Route path="/agency" element={
              <ProtectedRoute allowedRoles={["super_admin", "marketing_agency"]} requiresSubscription={false}>
                <PortalSuspense><Layout>
                  <PortalSuspense><AgencyMarketingPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/agency/campaigns/:campaignId" element={
              <ProtectedRoute allowedRoles={["super_admin", "marketing_agency"]} requiresSubscription={false}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MarketingCampaignDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/agency/contacts/:contactId" element={
              <ProtectedRoute allowedRoles={["super_admin", "marketing_agency"]} requiresSubscription={false}>
                <PortalSuspense><Layout>
                  <PortalSuspense><MarketingContactDetail /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* Affiliate self-service surface. */}
            <Route path="/affiliate" element={
              <ProtectedRoute allowedRoles={["super_admin", "affiliate"]} requiresSubscription={false}>
                <PortalSuspense><Layout>
                  <PortalSuspense><AffiliateDashboard /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/support" element={
              <ProtectedRoute allowedRoles={["super_admin", "support_staff"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminSupport /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/settings" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminSettingsPage /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/super-admin/ai" element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><SuperAdminAI /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            {/* Help Center — every authenticated role can open it. Broad
                allowedRoles so a super-admin/staff/agency/affiliate can still
                reach it by URL even though the avatar-menu entry is scoped to
                admin/user/crew. The whole feature sits behind HELP_CENTER_ENABLED
                (default ON): when off, the route is never registered — the
                matching sidebar entry (Sidebar.tsx) is gated on the same flag, so
                there is no reachable link left behind. */}
            {HELP_CENTER_ENABLED && (
            <Route path="/help" element={
              <ProtectedRoute allowedRoles={["admin","user","crew","super_admin","support_staff","marketing_agency","affiliate"]}>
                {/* Runtime kill switch on top of the build-time flag: a super-admin
                    can turn the Help Center off for everyone with no redeploy.
                    Resolves runtimeToggle ?? HELP_CENTER_ENABLED, so a normal ON
                    build renders immediately with no flash. */}
                <FeatureToggleGate feature="help_center">
                  <PortalSuspense><Layout>
                    <PortalSuspense><Help /></PortalSuspense>
                  </Layout></PortalSuspense>
                </FeatureToggleGate>
              </ProtectedRoute>
            } />
            )}
            {/* /whats-new — the release log, reached from the profile menu.
                Deliberately OUTSIDE the HELP_CENTER_ENABLED flag and the
                help_center runtime toggle: turning the guide library off is a
                decision about a support library, not about telling customers
                what changed. Same allowedRoles as /help so anyone who can sign
                in can read it. */}
            <Route path="/whats-new" element={
              <ProtectedRoute allowedRoles={["admin","user","crew","super_admin","support_staff","marketing_agency","affiliate"]}>
                <PortalSuspense><Layout>
                  <PortalSuspense><WhatsNew /></PortalSuspense>
                </Layout></PortalSuspense>
              </ProtectedRoute>
            } />
            <Route path="/forbidden" element={<PortalSuspense><NotAuthorized /></PortalSuspense>} />
            <Route path="/not-authorized" element={<PortalSuspense><NotAuthorized /></PortalSuspense>} />
            
            {/* /book and /lead retired (Phase 0): PublicBookingPage was a demo
               stub (hardcoded business, fake slots, no DB write -> silent lead
               loss) and PublicLeadForm a bare unbranded duplicate of
               /lead/:slug. Both moved to src/graveyard. Real intake is
               /lead/:slug (LeadCollectionPublic). */}

            <Route path="/learn" element={<PortalSuspense><DemoLibraryPage /></PortalSuspense>} />
            <Route path="/learn/:slug" element={<PortalSuspense><DemoLibraryPage /></PortalSuspense>} />
            <Route path="/super-admin/demo-library" element={<ProtectedRoute allowedRoles={["super_admin"]}><PortalSuspense><Layout><PortalSuspense><DemoLibraryManager /></PortalSuspense></Layout></PortalSuspense></ProtectedRoute>} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<PortalSuspense><NotFound /></PortalSuspense>} />
            </Routes>
          </BrowserRouter>
              </ErrorBoundary>
              </ConfirmDialogProvider>
            </GeocodingProvider>
            </PermissionProvider>
          </FeatureProvider>
        </AuthProvider>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
